import type { HistoryEvent, RequestStatus, VillageCertStatus, VillageCertification } from "./api";

/** ป้ายสถานะภาษาลาว — ใช้ร่วมกันทั้งคิวและหน้ารายละเอียด */
export const STATUS_LABEL_LO: Record<RequestStatus, string> = {
  draft: "ຮ່າງ",
  submitted: "ຍື່ນແລ້ວ",
  in_review: "ກຳລັງກວດ",
  needs_changes: "ຂໍຂໍ້ມູນເພີ່ມ",
  approved: "ອະນຸມັດແລ້ວ",
  signed: "ເຊັນແລ້ວ",
  issued: "ອອກເອກະສານແລ້ວ",
  rejected: "ປະຕິເສດ",
  cancelled: "ຍົກເລີກ",
};

export function statusBadgeClass(s: RequestStatus): string {
  switch (s) {
    case "approved":
    case "signed":
    case "issued":
      return "status-badge status-badge--success";
    case "rejected":
    case "cancelled":
      return "status-badge status-badge--danger";
    case "needs_changes":
      return "status-badge status-badge--pending";
    case "in_review":
      return "status-badge status-badge--info";
    default:
      return "status-badge";
  }
}

/** ยังรอเจ้าหน้าที่ทำอะไรสักอย่าง — คิว "ລໍຖ້າກວດ" */
export const OPEN_STATUSES: RequestStatus[] = ["submitted", "in_review", "needs_changes"];
/** ตัดสินใจแล้ว (อนุมัติ/เซ็น/ออกแล้ว) — สถิติ "ອະນຸມັດແລ້ວ" */
export const RESOLVED_STATUSES: RequestStatus[] = ["approved", "signed", "issued"];
/**
 * ทุกสถานะที่เจ้าหน้าที่มีสิทธิ์เห็น — **ไม่รวม `draft`**
 *
 * `GET /v1/admin/requests` ไม่กรอง status ให้เองถ้าไม่ส่ง — ยิงแบบไม่ใส่ตัวกรอง
 * จะได้ร่างคำร้องที่ผู้ขอยังไม่กดยื่นติดมาด้วย (เห็นข้อมูลที่เจ้าตัวยังไม่ตั้งใจแชร์)
 */
export const QUEUE_STATUSES: RequestStatus[] = [
  "submitted",
  "in_review",
  "needs_changes",
  "approved",
  "signed",
  "issued",
  "rejected",
  "cancelled",
];

/** เวลาที่ผ่านมาแบบภาษาลาว หยาบพอสำหรับคิวงาน ไม่ต้อง real-time นาทีเป๊ะ */
export function formatAgoLo(iso: string): string {
  const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60_000));
  if (mins < 1) return "ຫາກໍ່ນີ້";
  if (mins < 60) return `${mins} ນາທີກ່ອນ`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} ຊົ່ວໂມງກ່ອນ`;
  return `${Math.floor(hours / 24)} ມື້ກ່ອນ`;
}

/** ป้ายขั้นรับรองของนายบ้าน (ADR-0031) — สถานะย่อยของ `submitted` ไม่ใช่ RequestStatus */
export const VILLAGE_CERT_LABEL_LO: Record<VillageCertStatus, string> = {
  pending: "ລໍນາຍບ້ານຢັ້ງຢືນ",
  certified: "ນາຍບ້ານຢັ້ງຢືນແລ້ວ",
  returned: "ນາຍບ້ານສົ່ງຄືນໃຫ້ແກ້ໄຂ",
  unrecognized: "ນາຍບ້ານບໍ່ຮູ້ຈັກຜູ້ຂໍ",
  waived: "ຂ້າມຂັ້ນນາຍບ້ານ",
};

export function villageCertBadgeClass(s: VillageCertStatus): string {
  switch (s) {
    case "certified":
      return "status-badge status-badge--success";
    case "unrecognized":
      return "status-badge status-badge--danger";
    case "returned":
    case "pending":
      return "status-badge status-badge--pending";
    case "waived":
      return "status-badge";
  }
}

/** ป้ายแถวประวัติของขั้นนายบ้าน (`StatusHistoryEntry.event`) */
export const HISTORY_EVENT_LABEL_LO: Record<HistoryEvent, string> = {
  village_certified: "ນາຍບ້ານຢັ້ງຢືນແລ້ວ",
  village_returned: "ນາຍບ້ານສົ່ງຄືນ",
  village_unrecognized: "ນາຍບ້ານບໍ່ຮູ້ຈັກຜູ້ຂໍ",
  village_waived_overdue: "ຂ້າມຂັ້ນນາຍບ້ານ (ເກີນກຳນົດ)",
  village_waived_manual: "ຂ້າມຂັ້ນນາຍບ້ານ (ເຈົ້າໜ້າທີ່)",
};

/**
 * ยังรอนายบ้านอยู่ไหม (คำร้อง submitted + ขั้นนายบ้าน pending) — ตาแสงกดรับไม่ได้จนกว่าจะเกิน `due_at`
 * (เกินแล้วกดรับได้ server ข้ามขั้นให้ใน txn เดียวกัน)
 */
export function awaitingVillage(r: { status: RequestStatus; village_certification: VillageCertification | null }): boolean {
  return r.status === "submitted" && r.village_certification?.status === "pending";
}

/** เกินกำหนดของนายบ้านแล้ว (ณ เวลา `now`) — `due_at` ว่างถือว่ายังไม่เกิน */
export function villageOverdue(c: VillageCertification | null, now: number): boolean {
  return c?.status === "pending" && c.due_at != null && new Date(c.due_at).getTime() <= now;
}
